import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import 'dayjs/locale/ko';
import 'dayjs/locale/en';
import 'dayjs/locale/id';
import dayjs from 'dayjs';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoadingIndicator from './components/common/LoadingIndicator';
import { ErrorProvider } from './contexts/ErrorContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './hooks/useAuth';
import { PermissionProvider } from './contexts/PermissionContext';
import theme from './config/theme';
import { createCustomTheme } from './config/themeUtils';
import tokenService from './utils/tokenService';
import { checkVersionAndReload } from './utils/versionCheck';
// 전역 캐시 등록을 위해 import (모듈 로드 시 자동 등록)
import './utils/cache/globalCacheManager';
import './utils/cache/attachmentImageCache';
import './utils/cache/deliveryCache';
import './utils/cache/itemCache';
import './utils/cache/managerCache';
import './utils/cache/priceCache';
import './utils/cache/truckCache';
import './utils/cache/vendorCache';
import './utils/cache/buyerCache';
import './utils/cache/supplierCache';
import './utils/cache/locationCache';
import './utils/cache/uomConversionCache';
import './utils/cache/uomCache';
import './utils/cache/itemTaxonCache';
import './utils/cache/machineCache';
// 인보이스 페이지: lazy 청크에서 default export가 undefined로 풀리는 런타임 오류 방지
import InvoiceIssuance from './pages/orders/InvoiceIssuance';
import InvoiceManagement from './pages/orders/InvoiceManagement';

const Login = lazy(() => import('./components/Login'));
const MainLayout = lazy(() => import('./layouts/MainLayout'));

const MainPage = lazy(() => import('./pages/MainPage'));
const NoticesPage = lazy(() => import('./pages/notices/NoticesPage'));

const OrderRoutes = {
  OrderInput: lazy(() => import('./pages/orders/OrderInput')),
  OrderModify: lazy(() => import('./pages/orders/OrderModify')),
  OrderStatusTabs: lazy(() => import('./pages/orders/OrderStatusTabs')),
  OrderSearch: lazy(() => import('./pages/orders/OrderSearchPage')),
  SwatchManagement: lazy(() => import('./pages/orders/SwatchManagement')),
  InvoiceIssuance,
  InvoiceManagement
};

const DyeingRoutes = {
  DyeingWorkPage: lazy(() => import('./pages/work_request/DyeingWorkPage')),
  DyeingOperationPage: lazy(() => import('./pages/dyeing/DyeingOperationPage')),
  DyeingStatusPage: lazy(() => import('./pages/dyeing/DyeingStatusPage')),
  RecipeSearchPage: lazy(() => import('./pages/dyeing/RecipeSearchPage'))
};

const WindingRoutes = {
  WindingRequestPage: lazy(() => import('./pages/work_request/WindingRequestPage')),
  WindingOperationPage: lazy(() => import('./pages/winding/WindingOperationPage')),
  WindingStatusPage: lazy(() => import('./pages/winding/WindingStatusPage'))
};

const WindingDataPopup = lazy(() => import('./pages/WindingDataPopup'));
const DyeingWorkerPage = lazy(() => import('./pages/dyeing/DyeingWorkerPage'));

const ShippingRoutes = {
  ShippingRequestPage: lazy(() => import('./pages/work_request/GiRequestPage')),
  ShippingOperationPage: lazy(() => import('./pages/shipping/ShippingOperationPage')),
  ShippingTrackingPage: lazy(() => import('./pages/shipping/ShippingTrackingPage'))
};

const FinanceRoutes = {
  FinanceOverviewPage: lazy(() => import('./pages/finance/index')),
  PaymentManagement: lazy(() => import('./pages/finance/PaymentManagement'))
};

const InventoryRoutes = {
  BalancePage: lazy(() => import('./pages/inventory/BalancePage')),
  LotBatchPage: lazy(() => import('./pages/inventory/LotBatchPage/index')),
  TransactionHistoryPage: lazy(() => import('./pages/inventory/TransactionHistoryPage')),
  TransferPage: lazy(() => import('./pages/inventory/TransferPage')),
  AdjustmentPage: lazy(() => import('./pages/inventory/AdjustmentPage/index')),
  BatchInputPage: lazy(() => import('./pages/inventory/BatchInputPage'))
};

const ScmRoutes = {
  GoodsReceiptPage: lazy(() => import('./pages/scm/GoodsReceiptPage/index')),
  GoodsIssueListPage: lazy(() => import('./pages/scm/GoodsIssueListPage')),
  GoodsIssueRegistrationPage: lazy(() => import('./pages/scm/GoodsIssueRegistrationPage/index')),
  DeliveryInfoPage: lazy(() => import('./pages/scm/DeliveryInfoPage')),
  DeliveryStatusPage: lazy(() => import('./pages/scm/DeliveryStatusPage'))
};

const MasterDataRoutes = {
  ItemPage: lazy(() => import('./pages/master/ItemPage/index')),
  UomConversionPage: lazy(() => import('./pages/master/UomConversionPage')),
  LocationPage: lazy(() => import('./pages/master/LocationPage')),
  SupplierPage: lazy(() => import('./pages/master/SupplierPage/index')),
  ItemTaxonPage: lazy(() => import('./pages/master/ItemTaxonPage/index')),
  CustomerPage: lazy(() => import('./pages/master/CustomerPage/index')),
  BuyerPage: lazy(() => import('./pages/master/BuyerPage'))
};

const AdminRoutes = {
  Users: {
    RegistrationManagement: lazy(() => import('./pages/admin/users/RegistrationManagement')),
    PasswordReset: lazy(() => import('./pages/admin/users/PasswordReset')),
    AccessLogs: lazy(() => import('./pages/admin/users/AccessLogs')),
    MessageSend: lazy(() => import('./pages/admin/users/MessageSend'))
  },
  Standards: {
    ItemsManagement: lazy(() => import('./pages/admin/standards/ThreadsManagement')),
    CompaniesManagement: lazy(() => import('./pages/admin/standards/CompaniesManagement')),
    BuyersManagement: lazy(() => import('./pages/admin/standards/BuyersManagement')),
    PricesManagement: lazy(() => import('./pages/admin/standards/PricesManagement')),
    SizesManagement: lazy(() => import('./pages/admin/standards/SizesManagement')),
    MachineManagement: lazy(() => import('./pages/admin/standards/MachineManagement'))
  },
  Materials: {
    YarnManagement: lazy(() => import('./pages/admin/materials/YarnManagement')),
    DyeManagement: lazy(() => import('./pages/admin/materials/DyeManagement')),
    InventoryManagement: lazy(() => import('./pages/admin/materials/InventoryManagement'))
  },
  Bi: {
    Dashboard: lazy(() => import('./pages/admin/bi/BiDashboard'))
  }
};

if (typeof window !== 'undefined') {
  window.onerror = function (msg, url, lineNo, columnNo, error) {
    console.error('Global error:', { msg, url, lineNo, columnNo, error: error?.message });
    return !error?.message?.includes('persona data');
  };

  window.addEventListener('error', (event) => {
    console.error('Error event:', event.error?.message);
    if (event.error?.message?.includes('persona data')) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled rejection:', event.reason?.message);
    if (event.reason?.message?.includes('persona data')) {
      event.preventDefault();
      event.stopPropagation();
    }
  });
}

const handleGlobalError = (error) => {
  console.error('App error handler:', error?.message);
  if (error?.message?.includes('persona data')) {
    return false;
  }
  return true;
};

const routerOptions = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

const MainRoutes = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary>
      <MainLayout>
        <Suspense fallback={<LoadingIndicator isLoading={true} message={t('common.loading')} />}>
          <Routes>
            <Route path="/" element={<MainPage />} />
            <Route path="notices" element={<NoticesPage />} />

            <Route path="orders">
              <Route path="input" element={<OrderRoutes.OrderInput />} />
              <Route path="modify" element={<OrderRoutes.OrderModify />} />
              <Route path="work-order" element={<OrderRoutes.OrderStatusTabs />} />
              <Route path="search" element={<OrderRoutes.OrderSearch />} />
              <Route path="swatch-management" element={<OrderRoutes.SwatchManagement />} />
              <Route path="invoice-issuance" element={<OrderRoutes.InvoiceIssuance />} />
              <Route path="invoice-management" element={<OrderRoutes.InvoiceManagement />} />
            </Route>

            <Route path="dyeing">
              <Route path="work" element={<DyeingRoutes.DyeingWorkPage />} />
              <Route path="operation" element={<DyeingRoutes.DyeingOperationPage />} />
              <Route path="status" element={<DyeingRoutes.DyeingStatusPage />} />
              <Route path="recipe-search" element={<DyeingRoutes.RecipeSearchPage />} />
            </Route>

            <Route path="winding">
              <Route path="request" element={<WindingRoutes.WindingRequestPage />} />
              <Route path="operation" element={<WindingRoutes.WindingOperationPage />} />
              <Route path="status" element={<WindingRoutes.WindingStatusPage />} />
            </Route>

            <Route path="shipping">
              <Route path="request" element={<ShippingRoutes.ShippingRequestPage />} />
              <Route path="operation" element={<ShippingRoutes.ShippingOperationPage />} />
              <Route path="tracking" element={<ShippingRoutes.ShippingTrackingPage />} />
            </Route>

            <Route path="finance">
              <Route path="payment-management" element={<FinanceRoutes.PaymentManagement />} />
            </Route>

            <Route path="inventory">
              <Route path="balance" element={<InventoryRoutes.BalancePage />} />
              <Route path="lot-batch" element={<InventoryRoutes.LotBatchPage />} />
              <Route path="transaction-history" element={<InventoryRoutes.TransactionHistoryPage />} />
              <Route path="adjustment" element={<InventoryRoutes.AdjustmentPage />} />
              <Route path="batch-input" element={<InventoryRoutes.BatchInputPage />} />
            </Route>

            <Route path="scm">
              <Route path="goods-receipt" element={<ScmRoutes.GoodsReceiptPage />} />
              <Route path="goods-issue-list" element={<ScmRoutes.GoodsIssueListPage />} />
              <Route path="goods-issue-registration" element={<ScmRoutes.GoodsIssueRegistrationPage />} />
              <Route path="delivery-info" element={<ScmRoutes.DeliveryInfoPage />} />
              <Route path="delivery-status" element={<ScmRoutes.DeliveryStatusPage />} />
            </Route>

            <Route path="master">
              <Route path="item" element={<MasterDataRoutes.ItemPage />} />
              <Route path="uom-conversion" element={<MasterDataRoutes.UomConversionPage />} />
              <Route path="location" element={<MasterDataRoutes.LocationPage />} />
              <Route path="supplier" element={<MasterDataRoutes.SupplierPage />} />
              <Route path="item-taxon" element={<MasterDataRoutes.ItemTaxonPage />} />
              <Route path="customer" element={<MasterDataRoutes.CustomerPage />} />
              <Route path="buyer" element={<MasterDataRoutes.BuyerPage />} />
            </Route>

            <Route path="admin">
              <Route path="users">
                <Route path="registration" element={<AdminRoutes.Users.RegistrationManagement />} />
                <Route path="reset-password" element={<AdminRoutes.Users.PasswordReset />} />
                <Route path="access-logs" element={<AdminRoutes.Users.AccessLogs />} />
                <Route path="send-message" element={<AdminRoutes.Users.MessageSend />} />
              </Route>

              <Route path="standards">
                <Route path="threads" element={<AdminRoutes.Standards.ItemsManagement />} />
                <Route path="companies" element={<AdminRoutes.Standards.CompaniesManagement />} />
                <Route path="buyers" element={<AdminRoutes.Standards.BuyersManagement />} />
                <Route path="prices" element={<AdminRoutes.Standards.PricesManagement />} />
                <Route path="sizes" element={<AdminRoutes.Standards.SizesManagement />} />
                <Route path="machine" element={<AdminRoutes.Standards.MachineManagement />} />
              </Route>

              <Route path="materials">
                <Route path="yarn" element={<AdminRoutes.Materials.YarnManagement />} />
                <Route path="dye" element={<AdminRoutes.Materials.DyeManagement />} />
                <Route path="inventory" element={<AdminRoutes.Materials.InventoryManagement />} />
              </Route>

              <Route path="bi" element={<AdminRoutes.Bi.Dashboard />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </MainLayout>
    </ErrorBoundary>
  );
};

function App() {
  // i18n 초기화
  useEffect(() => {
    import('./config/i18n');
  }, []);

  // 배포된 앱 버전과 현재 번들 버전 비교 후 다르면 강제 새로고침
  useEffect(() => {
    checkVersionAndReload();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersionAndReload();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const { t, i18n } = useTranslation();

  // 테마 상태 관리
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('user_theme') || 'default';
    const savedColor = localStorage.getItem('user_color') || 'default';
    // default이면 기본 테마 사용
    if (savedTheme === 'default' && savedColor === 'default') {
      return theme;
    }
    return createCustomTheme(savedTheme, savedColor);
  });

  // 테마 변경 이벤트 리스너
  useEffect(() => {
    const handleThemeChange = (event) => {
      const { theme: newTheme, color: newColor } = event.detail || {};
      const themeValue = newTheme || localStorage.getItem('user_theme') || 'default';
      const colorValue = newColor || localStorage.getItem('user_color') || 'default';
      
      // default이면 기본 테마 사용
      if (themeValue === 'default' && colorValue === 'default') {
        setCurrentTheme(theme);
      } else {
        setCurrentTheme(createCustomTheme(themeValue, colorValue));
      }
    };

    window.addEventListener('themeChanged', handleThemeChange);
    
    // 초기 로드 시 localStorage에서 테마 확인
    const savedTheme = localStorage.getItem('user_theme') || 'default';
    const savedColor = localStorage.getItem('user_color') || 'default';
    if (savedTheme !== 'default' || savedColor !== 'default') {
      setCurrentTheme(createCustomTheme(savedTheme, savedColor));
    }

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  // dayjs 로케일을 i18n 언어에 따라 동적으로 설정
  useEffect(() => {
    const locale = i18n.language === 'ko' ? 'ko' : i18n.language === 'id' ? 'id' : 'en';
    dayjs.locale(locale);
  }, [i18n.language]);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    // 앱 시작 시 손상된 토큰 정리
    tokenService.cleanupCorruptedTokens();
  }, []);

  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args) => {
      if (args.some(arg =>
        typeof arg === 'string' &&
        (arg.includes('persona data') ||
          arg.includes('contentscript.js'))
      )) {
        return;
      }
      originalError.apply(console, args);
    };

    console.warn = (...args) => {
      if (args.some(arg =>
        typeof arg === 'string' &&
        (arg.includes('persona data') ||
          arg.includes('contentscript.js'))
      )) {
        return;
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  return (
    <ThemeProvider theme={currentTheme}>
      <ErrorProvider>
        <ToastProvider>
        <LocalizationProvider
          dateAdapter={AdapterDayjs} 
          adapterLocale={i18n.language === 'ko' ? 'ko' : i18n.language === 'id' ? 'id' : 'en'}
        >
          <div className="App" onError={handleGlobalError}>
            <ErrorBoundary>
              <BrowserRouter {...routerOptions}>
                <AuthProvider>
                  <PermissionProvider>
                    <AnimatePresence mode="wait">
                    <Suspense fallback={<LoadingIndicator isLoading={true} message={t('common.loading')} />}>
                      <Routes>
                        {/* 공개 라우트 (인증 불필요) */}
                        <Route path="/winding-data/:locale/:windingId" element={<WindingDataPopup />} />
                        <Route path="/winding-data/:windingId" element={<WindingDataPopup />} />
                        <Route path="/winding_id/:windingId" element={<WindingDataPopup />} />
                        <Route path="/winding_id/:locale/:windingId" element={<WindingDataPopup />} />
                        <Route path="/dyeing-worker/:locale" element={<DyeingWorkerPage />} />
                        <Route path="/dyeing-worker" element={<DyeingWorkerPage />} />
                        
                        {/* 인증이 필요한 라우트 */}
                        <Route path="/" element={<Login />} />
                        <Route
                          path="/main/*"
                          element={
                            <ProtectedRoute>
                              <MainRoutes />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Suspense>
                  </AnimatePresence>
                  </PermissionProvider>
                </AuthProvider>
              </BrowserRouter>
            </ErrorBoundary>
          </div>
        </LocalizationProvider>
        </ToastProvider>
      </ErrorProvider>
    </ThemeProvider>
  );
}

export default App;